import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getBackendUrl } from '../lib/apiUrls';
import { createWorkspaceClient } from '../lib/workspaceClient';

const NotificationsContext = createContext({
  unreadCount: 0,
  isLoadingNotificationSummary: false,
  refreshNotificationSummary: async () => 0,
});

export function NotificationsProvider({ currentUser, children }) {
  const workspaceClient = useMemo(() => createWorkspaceClient(getBackendUrl()), []);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoadingNotificationSummary, setIsLoadingNotificationSummary] = useState(false);

  const refreshNotificationSummary = useCallback(async () => {
    if (!currentUser?.userId) {
      setUnreadCount(0);
      return 0;
    }

    setIsLoadingNotificationSummary(true);
    try {
      const payload = await workspaceClient.getNotifications(currentUser.userId, {
        status: 'all',
        limit: 20,
      });
      const nextUnreadCount =
        typeof payload?.unreadCount === 'number' && Number.isFinite(payload.unreadCount)
          ? payload.unreadCount
          : 0;
      setUnreadCount(nextUnreadCount);
      return nextUnreadCount;
    } catch {
      return 0;
    } finally {
      setIsLoadingNotificationSummary(false);
    }
  }, [currentUser?.userId, workspaceClient]);

  useEffect(() => {
    void refreshNotificationSummary();
  }, [refreshNotificationSummary]);

  const value = useMemo(
    () => ({
      unreadCount,
      isLoadingNotificationSummary,
      refreshNotificationSummary,
    }),
    [isLoadingNotificationSummary, refreshNotificationSummary, unreadCount]
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}

export function useNotificationsSummary() {
  return useContext(NotificationsContext);
}
